/**
 * RBAC policy tables: action permissions, UI view access, field-bundle visibility,
 * and role-assignment allowlists. Lookup helpers merge qualifiers across additive roles.
 */
import { PLATFORM_ROLE_TYPE } from '@/consts/common';
import { IUserListApiField, PlatformRoleType } from '@/services/users/types';
import {
  IAccessQualifier,
  IOrganizationFieldBundle,
  IPermissionAction,
  IUiView,
  IUiViewAccess,
  IUserFieldBundle,
  IVisibilityQualifier,
  ORGANIZATION_FIELD_BUNDLE,
  PERMISSION_ACTION,
  UI_VIEW,
  USER_FIELD_BUNDLE,
} from './types';
import { IOrganizationApiField } from '@/services/organizations/types';

const {
  USER,
  ORG_ADMIN,
  ORG_OWNER,
  PLATFORM_AUDITOR,
  PLATFORM_SUPPORT,
  PLATFORM_ADMIN,
} = PLATFORM_ROLE_TYPE;

type RolePolicyRow = Record<PlatformRoleType, IAccessQualifier>;
type UiViewPolicyRow = Record<PlatformRoleType, IUiViewAccess>;
type VisibilityPolicyRow = Record<PlatformRoleType, IVisibilityQualifier>;

const none = 'none' as const;
const self = 'self' as const;
const limited = 'limited' as const;
const scoped = 'scoped' as const;
const full = 'full' as const;

const hidden = 'hidden' as const;
const selfVisible = 'selfVisible' as const;
const scopedVisible = 'scopedVisible' as const;
const visible = 'visible' as const;

const ACCESS_QUALIFIER_RANK: Record<IAccessQualifier, number> = {
  none: 0,
  self: 1,
  scoped: 2,
  full: 3,
};

/** Maps each permission action to per-role access qualifiers from the RBAC spec. */
export const ACTION_PERMISSIONS: Record<IPermissionAction, RolePolicyRow> = {
  [PERMISSION_ACTION.viewUserDirectory]: {
    [USER]: scoped,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewUserDetails]: {
    [USER]: scoped,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.editUserProfile]: {
    [USER]: self,
    [ORG_ADMIN]: self,
    [ORG_OWNER]: self,
    [PLATFORM_AUDITOR]: self,
    [PLATFORM_SUPPORT]: self,
    [PLATFORM_ADMIN]: self,
  },
  [PERMISSION_ACTION.transferUserToOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.deleteUserAccount]: {
    [USER]: self,
    [ORG_ADMIN]: self,
    [ORG_OWNER]: self,
    [PLATFORM_AUDITOR]: self,
    [PLATFORM_SUPPORT]: self,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.modifyPlatformRoles]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.createOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewOrganizationProfile]: {
    [USER]: scoped,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.updateOrganizationProfile]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.updateOrganizationSettings]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.deleteOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.updateOrganizationBilling]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewOrganizationUsers]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.inviteUserToOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.resendInvitation]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.removeUserFromOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignOrgAdminRole]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignOrgOwnerRole]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignPlatformRole]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewLicense]: {
    [USER]: self,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewOrganizationLicensePool]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignLicenseToOrgUser]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.viewScheduleHoursToOrgUser]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignScheduleHoursToOrgUser]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.assignLicenseToIndependentUser]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.distributeLicensesToOrganization]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [PERMISSION_ACTION.requestMoreLicenses]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: none,
  },
  [PERMISSION_ACTION.requestLicenseRenewal]: {
    [USER]: self,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: none,
  },
  [PERMISSION_ACTION.requestLicenseUpgrade]: {
    [USER]: self,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: none,
  },
};

export const UI_VIEW_PERMISSIONS: Record<IUiView, UiViewPolicyRow> = {
  [UI_VIEW.userDetailsPage]: {
    [USER]: scoped,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: limited,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.userDirectory]: {
    [USER]: scoped,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.organizationProfilePage]: {
    [USER]: limited,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.organizationSettingsPage]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: none,
    [PLATFORM_SUPPORT]: none,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.organizationUsersTable]: {
    [USER]: none,
    [ORG_ADMIN]: scoped,
    [ORG_OWNER]: scoped,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.organizationsTable]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
  [UI_VIEW.independentUsersTable]: {
    [USER]: none,
    [ORG_ADMIN]: none,
    [ORG_OWNER]: none,
    [PLATFORM_AUDITOR]: full,
    [PLATFORM_SUPPORT]: full,
    [PLATFORM_ADMIN]: full,
  },
};

export const USER_FIELD_BUNDLE_VISIBILITY: Record<IUserFieldBundle, VisibilityPolicyRow> = {
  [USER_FIELD_BUNDLE.identity]: {
    [USER]: scopedVisible,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [USER_FIELD_BUNDLE.contact]: {
    [USER]: scopedVisible,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [USER_FIELD_BUNDLE.phone]: {
    [USER]: selfVisible,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: hidden,
    [PLATFORM_ADMIN]: visible,
  },
  [USER_FIELD_BUNDLE.lifecycle]: {
    [USER]: selfVisible,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [USER_FIELD_BUNDLE.authMetadata]: {
    [USER]: selfVisible,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: hidden,
    [PLATFORM_AUDITOR]: hidden,
    [PLATFORM_SUPPORT]: hidden,
    [PLATFORM_ADMIN]: visible,
  },
  [USER_FIELD_BUNDLE.systemFields]: {
    [USER]: hidden,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: hidden,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
};

export const ORGANIZATION_FIELD_BUNDLE_VISIBILITY: Record<IOrganizationFieldBundle,
VisibilityPolicyRow> = {
  [ORGANIZATION_FIELD_BUNDLE.identity]: {
    [USER]: scopedVisible,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [ORGANIZATION_FIELD_BUNDLE.billing]: {
    [USER]: selfVisible,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [ORGANIZATION_FIELD_BUNDLE.settings]: {
    [USER]: hidden,
    [ORG_ADMIN]: scopedVisible,
    [ORG_OWNER]: scopedVisible,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [ORGANIZATION_FIELD_BUNDLE.metadata]: {
    [USER]: hidden,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: hidden,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [ORGANIZATION_FIELD_BUNDLE.lifecycle]: {
    [USER]: hidden,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: hidden,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
  [ORGANIZATION_FIELD_BUNDLE.systemFields]: {
    [USER]: hidden,
    [ORG_ADMIN]: hidden,
    [ORG_OWNER]: hidden,
    [PLATFORM_AUDITOR]: visible,
    [PLATFORM_SUPPORT]: visible,
    [PLATFORM_ADMIN]: visible,
  },
};

export const USER_BUNDLE_TO_API_FIELDS: Record<IUserFieldBundle, readonly IUserListApiField[]> = {
  [USER_FIELD_BUNDLE.identity]: [],
  [USER_FIELD_BUNDLE.contact]: ['email'],
  [USER_FIELD_BUNDLE.phone]: ['countryCode', 'phone'],
  [USER_FIELD_BUNDLE.lifecycle]: ['lifecycleStatus', 'joinedAt', 'invitedBy'],
  [USER_FIELD_BUNDLE.authMetadata]: ['lastLoggedInAt'],
  [USER_FIELD_BUNDLE.systemFields]: ['createdAt', 'updatedAt', 'deleted'],
};

export const ORGANIZATION_BUNDLE_TO_API_FIELDS: Record<
IOrganizationFieldBundle, readonly IOrganizationApiField[]> = {
  [ORGANIZATION_FIELD_BUNDLE.identity]: ['name', 'description'],
  [ORGANIZATION_FIELD_BUNDLE.billing]: ['billingEmail', 'billingAddress'],
  [ORGANIZATION_FIELD_BUNDLE.settings]: ['settings'],
  [ORGANIZATION_FIELD_BUNDLE.metadata]: ['metadata'],
  [ORGANIZATION_FIELD_BUNDLE.lifecycle]: ['createdAt'],
  [ORGANIZATION_FIELD_BUNDLE.systemFields]: ['updatedAt', 'deleted'],
};

export const PLATFORM_ADMIN_ASSIGNABLE_ROLES: PlatformRoleType[] = [
  PLATFORM_ADMIN,
  PLATFORM_AUDITOR,
  PLATFORM_SUPPORT,
  ORG_ADMIN,
];

export const PLATFORM_SUPPORT_ASSIGNABLE_ROLES: PlatformRoleType[] = [
  ORG_ADMIN,
];

export const ORG_OWNER_ASSIGNABLE_ROLES: PlatformRoleType[] = [
  ORG_ADMIN,
];

/** Picks the strongest access qualifier when the viewer has multiple roles. */
export function mergeAccessQualifiers(qualifiers: IAccessQualifier[]): IAccessQualifier {
  return qualifiers.reduce(
    (highest, current) => (
      ACCESS_QUALIFIER_RANK[current] > ACCESS_QUALIFIER_RANK[highest] ? current : highest
    ),
    none,
  );
}

/** Returns the access qualifier for one role and one action. */
export function getActionQualifierForRole(
  action: IPermissionAction,
  role: PlatformRoleType,
): IAccessQualifier {
  return ACTION_PERMISSIONS[action][role];
}

/** Returns the UI view access level for one role. */
export function getUiViewAccessForRole(view: IUiView, role: PlatformRoleType): IUiViewAccess {
  return UI_VIEW_PERMISSIONS[view][role];
}

/** Returns field-bundle visibility for one user bundle and one role. */
export function getUserFieldVisibilityForRole(
  bundle: IUserFieldBundle,
  role: PlatformRoleType,
): IVisibilityQualifier {
  return USER_FIELD_BUNDLE_VISIBILITY[bundle][role];
}

/** Returns field-bundle visibility for one organization bundle and one role. */
export function getOrganizationFieldVisibilityForRole(
  bundle: IOrganizationFieldBundle,
  role: PlatformRoleType,
): IVisibilityQualifier {
  return ORGANIZATION_FIELD_BUNDLE_VISIBILITY[bundle][role];
}
